import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import environment from 'platform/utilities/environment';
import { connectDrupalStaticDataFileVaHealthServices } from 'platform/site-wide/drupal-static-data/source-files/va-health-services/connect';
import {
  normalizeSearchTerm,
  singularizePhrase,
} from '../utils/normalizeSearchTerm';

// Note: this file is hard-coded with data from https://www.va.gov/data/cms/va-healthcare-services.json
// It should be updated periodically, especially when local changes are made that affect the matching logic so we can be
// sure our code handles what services actually exist in prod
import vaHealthcareServices from '../tests/hooks/test-va-healthcare-services.json';

/** VA health service example structure
 * [
    'Mental health care',     0. Name (string)
    'Behavioral health',      1. AKA (string)
    [                         2. Common conditions (string[])
      'addiction',
      'depression',
      'anxiety',
      'trauma',
      'PTSD',
      'bipolar disorder',
      'schizophrenia',
      'OCD'
    ],
    'mentalHealth',            3. API ID (string)
    'Mental health care',      4. Service type of care (string)
    true,                      5. Show for Vet Center (boolean)
    false,                     6. Show for VBA facilities (boolean)
    true,                      7. Show for VAMC facilities (boolean)
    false,                     8. Show for TRICARE (boolean)
    443,                       9. Service ID (number)
    'Regular description',     10. Description (string)
    'TRICARE description',     11. TRICARE description (string)
    ['mental health cares'],   12. Plural equivalents (string[])
    ['mentl health'],          13. Misspellings (string[])
    ['behavioral healthcare']  14. Synonyms (string[])
  ]
 */

export const FACILITY_TYPE_FILTERS = {
  VET_CENTER: 'vet_center',
  VBA: 'vba',
  VAMC: 'vamc',
  TRICARE: 'tricare',
};

const escapeForRegex = term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Corpus-derived stop words: every English filler word that appears at least
// three times across the service `description` + `tricareDescription` fields
// (the only free-form text the matcher reads). Filtering these from user input
// keeps queries like "and the" from dragging in unrelated services on a
// single-token hit. Words that never appear in the corpus are intentionally
// omitted — filtering them would be a no-op since nothing would match them.
// Domain words (care, health, services, treatment, ...) are also intentionally
// omitted: users legitimately search for them.
//
// To refresh this list when the data changes, retoken the description corpus,
// keep tokens with count >= 3, and exclude domain vocabulary by hand.
const STOP_WORDS = new Set([
  'a',
  'also',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'for',
  'from',
  'get',
  'have',
  'here',
  'if',
  'in',
  'is',
  'just',
  'like',
  'may',
  'more',
  'of',
  'on',
  'or',
  'other',
  'our',
  'such',
  'that',
  // 'the',
  'their',
  'they',
  'through',
  'to',
  'we',
  'when',
  'who',
  'will',
  'with',
  'you',
  'your',
  'youre',
]);

// Two-bucket grouping for matched services:
//  - "primary" hits: matches in the service's name, AKA, curated common
//    conditions, curated pluralEquivalents, or curated synonyms — all
//    structured fields whose contents point at this specific service.
//  - "secondary" hits: matches that only show up in the free-form description
//    or TRICARE description.
// The bucket is a hard tier: every primary match sorts above every secondary
// match. Within a bucket, sortMatches ranks by matchScore (below) descending,
// then alphabetically. Misspellings (service[13]) are deliberately NOT checked
// yet.
const PRIMARY_FIELDS = [0, 1, 2, 12, 14];
const SECONDARY_FIELDS = [10, 11];

// Relevance scoring used to RANK matches within a bucket (VACMS-24484). A match
// is scored by how completely the typed term matches a field: a complete term
// (the whole field value, or a whole-word prefix of it) outranks an interior-
// token match. That per-field "kind" is weighted by field priority, so e.g. a
// name match outranks a synonym match. The result surfaces the most relevant,
// non-tokenized matches first instead of a flat alphabetical list.
const KIND = { EXACT: 100, PREFIX: 50, TOKEN: 1, NONE: 0 };
const FIELD_WEIGHTS = {
  0: 2, // name
  1: 2, // AKA
  2: 2, // common conditions
  14: 2, // synonyms
  12: 2, // plural equivalents
  10: 1, // description
  11: 1, // TRICARE description
};

export const selectServiceDataSource = (fetchedData, canUseFixtureFallback) => {
  // Prefer the platform's fetched data when it has services.
  if (fetchedData?.data?.length) {
    return fetchedData;
  }
  // Otherwise fall back to the bundled fixture, but only where that's allowed
  // (e.g. localhost); elsewhere pass the (empty) fetched data through.
  if (canUseFixtureFallback) {
    return vaHealthcareServices;
  }
  return fetchedData || {};
};

/**
 * Build a case-insensitive, word-boundary "starts-with" matcher for a single
 * normalized value (one word or a multi-word phrase). We also accept the
 * value's heuristic singular form so e.g. "dentists" matches curated data that
 * only carries "dentist"; curated synonym / misspelling / pluralEquivalent
 * arrays cover the irregular cases.
 * @param { string } normalized a value already run through normalizeSearchTerm
 * @returns { RegExp }
 */
const buildMatcher = normalized => {
  const singular = singularizePhrase(normalized);
  const escaped = escapeForRegex(normalized);
  // `\b` enforces a word-boundary so we only match at the start of a word.
  const pattern =
    normalized === singular
      ? `\\b${escaped}`
      : `\\b(?:${escaped}|${escapeForRegex(singular)})`;
  return new RegExp(pattern, 'i');
};

/**
 * Test whether a prepared matcher hits a single service field, which may be a
 * string (e.g. a name) or a string array (e.g. common conditions).
 * @param { RegExp } matcher
 * @param { string | string[] } serviceData
 * @returns { boolean }
 */
const fieldMatches = (matcher, serviceData) => {
  if (!serviceData || !serviceData?.length) {
    return false;
  }

  const testValue = value => matcher.test(normalizeSearchTerm(value));

  return Array.isArray(serviceData)
    ? serviceData.some(testValue)
    : testValue(serviceData);
};

/** Given a term and a VA health service, decide whether the term matches and
 * which bucket (primary / secondary) the service lands in. The term is
 * tokenized simply by space and stop words ("and", "for", "of", ...) are
 * dropped; matching against the remaining tokens is controlled by the
 * `requireAllTokens` flag — `true` (default) means every token must hit
 * somewhere in the bucket (logical AND); `false` means any single token hit
 * is enough (logical OR). Each token may match a different field within the
 * bucket. `filterMatches` runs AND first and falls back to OR when AND yields
 * nothing, so callers normally don't pass this directly.
 *
 * The bucket is set purely by WHICH fields matched: a hit in name, AKA,
 * common conditions, pluralEquivalents, or synonyms makes this a primary
 * match; a hit only in description or TRICARE description makes it secondary.
 *
 * Example: term = "men"
 * If a name contains the word "Mental," it matches (prefix at a word boundary).
 * If a common condition is "service member," it matches ("member").
 * If the AKA has the word "women," it's not a match (word-boundary anchored).
 * @param { string } term
 * @param { VA health service } service
 * @param { boolean } [requireAllTokens=true]
 * @returns { VA health service } a copy of the service carrying a primaryMatch
 *   or secondaryMatch boolean when it matched
 */
const determineIfServiceMatches = (term, service, requireAllTokens = true) => {
  const serviceToMatch = [...service];

  const normalizedTerm = normalizeSearchTerm(term);
  if (!normalizedTerm) {
    return serviceToMatch;
  }

  // Drop stop words ("and", "the", "for", ...) so common filler doesn't pull
  // in unrelated services on a single-token hit. Curated multi-word values
  // that legitimately contain a stop word — e.g. "addictions and substance
  // uses" — still match via their meaningful tokens.
  const tokens = normalizedTerm
    .split(' ')
    .filter(Boolean)
    .filter(token => !STOP_WORDS.has(token));

  // If the user typed only stop words there's nothing meaningful to match on.
  if (!tokens.length) {
    return serviceToMatch;
  }

  const tokenMatchers = tokens.map(buildMatcher);

  // A field group "hits" when the configured token combinator is satisfied:
  // AND (requireAllTokens) requires every token to match at a word boundary in
  // at least one of the bucket's fields; OR requires only a single token hit.
  // Each token may match a different field within the bucket. Check primary
  // first so a service is promoted out of the secondary bucket whenever the
  // primary bucket satisfies the combinator.
  const combinator = requireAllTokens ? 'every' : 'some';
  const matchedIn = fieldIndices =>
    tokenMatchers[combinator](matcher =>
      fieldIndices.some(index => fieldMatches(matcher, service[index])),
    );

  if (matchedIn(PRIMARY_FIELDS)) {
    serviceToMatch.primaryMatch = true;
  } else if (matchedIn(SECONDARY_FIELDS)) {
    serviceToMatch.secondaryMatch = true;
  }

  if (serviceToMatch.primaryMatch || serviceToMatch.secondaryMatch) {
    // Rank within the bucket by how completely the term matches (VACMS-24484).
    // Score the FULL normalized term against each field: an exact field value
    // (or the term's singular) scores highest, a whole-word prefix next, an
    // interior word-boundary hit lowest. `coveredTokens` is a small tiebreaker
    // toward matches that cover more of a multi-word query.
    const singularTerm = singularizePhrase(normalizedTerm);
    const phraseMatcher = buildMatcher(normalizedTerm);
    const phraseKind = fieldValue => {
      if (!fieldValue) {
        return KIND.NONE;
      }
      const values = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
      return values.reduce((best, value) => {
        const normalized = normalizeSearchTerm(value);
        if (!normalized) {
          return best;
        }
        if (normalized === normalizedTerm || normalized === singularTerm) {
          return Math.max(best, KIND.EXACT);
        }
        if (
          normalized.startsWith(`${normalizedTerm} `) ||
          normalized.startsWith(`${singularTerm} `)
        ) {
          return Math.max(best, KIND.PREFIX);
        }
        if (phraseMatcher.test(normalized)) {
          return Math.max(best, KIND.TOKEN);
        }
        return best;
      }, KIND.NONE);
    };

    const scoredFields = serviceToMatch.primaryMatch
      ? PRIMARY_FIELDS
      : SECONDARY_FIELDS;
    const phraseScore = scoredFields.reduce(
      (sum, index) => sum + FIELD_WEIGHTS[index] * phraseKind(service[index]),
      0,
    );
    const coveredTokens = tokenMatchers.filter(matcher =>
      scoredFields.some(index => fieldMatches(matcher, service[index])),
    ).length;
    serviceToMatch.matchScore = phraseScore * 10 + coveredTokens;
  }

  return serviceToMatch;
};

/**
 * Alphabetizes a given array of VA health services
 * by the name of the service (first index)
 * @param { VA health service[] } services
 * @returns { VA health service[] }
 */
const alphabetizeServices = services => {
  return services.sort((a, b) => {
    if (a[0].toLowerCase() < b[0].toLowerCase()) {
      return -1;
    }

    if (a[0].toLowerCase() > b[0].toLowerCase()) {
      return 1;
    }

    return 0;
  });
};

/**
 * Comparator: rank by matchScore (descending), then alphabetically by service
 * name as a stable tiebreaker so equally-relevant matches stay predictable.
 * @param { VA health service } a
 * @param { VA health service } b
 * @returns { number }
 */
const byScoreThenName = (a, b) => {
  const scoreA = a.matchScore || 0;
  const scoreB = b.matchScore || 0;

  if (scoreB !== scoreA) {
    return scoreB - scoreA;
  }

  return a[0].toLowerCase().localeCompare(b[0].toLowerCase());
};

/**
 * Given an array of VA health services with primaryMatch or secondaryMatch
 * keys, return primary matches first and secondary matches second. The bucket
 * is the hard tier; within each bucket, services are ranked by matchScore (most
 * complete matches first), then alphabetically by name.
 * @param { VA health service[] } matches
 * @returns { VA health service[] } sortedMatches
 */
const sortMatches = matches => {
  const primaryMatches = matches.filter(match => match.primaryMatch);
  const secondaryMatches = matches.filter(match => match.secondaryMatch);
  const sortedMatches = [];

  if (primaryMatches?.length) {
    sortedMatches.push(...primaryMatches.sort(byScoreThenName));
  }

  if (secondaryMatches?.length) {
    sortedMatches.push(...secondaryMatches.sort(byScoreThenName));
  }

  return sortedMatches;
};

/**
 * Returns all available services for a given facility type using
 * the booleans that indicate which service they belong to
 * @param { VA health service[] } allServices
 * @param { string } facilityType
 * @returns { VA health service[] }
 */
export const filterServicesByFacilityType = (allServices, facilityType) => {
  return allServices.filter(
    service =>
      (service[5] && facilityType === FACILITY_TYPE_FILTERS.VET_CENTER) ||
      (service[6] && facilityType === FACILITY_TYPE_FILTERS.VBA) ||
      (service[7] && facilityType === FACILITY_TYPE_FILTERS.VAMC) ||
      (service[8] && facilityType === FACILITY_TYPE_FILTERS.TRICARE),
  );
};

/**
 * Look through all services for a given facility, check for "starts with"
 * matches, sort alphabetically by priority, and return. Multi-token queries
 * use AND first (every meaningful token must hit somewhere in a bucket); if
 * AND yields nothing we fall back to OR (any single-token hit). This is a
 * stopgap until we score services by phrase/token coverage and rank rather
 * than bucket.
 * @param { VA health service[] } allServicesByFacilityType
 * @param { string } term
 * @returns { VA health service[] or [] }
 */
export const filterMatches = (allServicesByFacilityType, term) => {
  const collectMatches = requireAllTokens => {
    const out = [];
    allServicesByFacilityType.forEach(service => {
      const checkedService = determineIfServiceMatches(
        term,
        service,
        requireAllTokens,
      );
      if (checkedService.primaryMatch || checkedService.secondaryMatch) {
        out.push(checkedService);
      }
    });
    return out;
  };

  let matchedServices = collectMatches(true);
  if (!matchedServices.length) {
    matchedServices = collectMatches(false);
  }

  if (!matchedServices.length) {
    return [];
  }

  if (matchedServices.length === 1) {
    return matchedServices;
  }

  return sortMatches(matchedServices);
};

export default function useServiceType() {
  const dispatch = useDispatch();
  const ALL_SERVICES_VAMC = ['All VA health services'];

  // Prefer the platform's drupal-static-data fetch (Redux). Fall back to the
  // bundled fixture only for localhost workflows where /data/cms/* may not be
  // available (e.g. `yarn watch` without content-build).
  const fetched = useSelector(
    state => state.drupalStaticData.vaHealthServicesData,
  );
  const selector = selectServiceDataSource(fetched, environment.isLocalhost());

  useEffect(() => {
    connectDrupalStaticDataFileVaHealthServices(dispatch);
  }, [dispatch]);

  /**
   * When the autosuggest component is first loaded,
   * return all services for that facility type alphabetized
   * @param { VA health service[] } filteredServices
   * @param { string } facilityType
   * @returns { VA health service[]} sortedServices
   */
  const populateInitialServiceList = (
    allServicesByFacilityType,
    facilityType,
  ) => {
    const sortedServices = alphabetizeServices(allServicesByFacilityType);

    // If the facility type is VAMC, add the "All VA health services" option
    if (facilityType === FACILITY_TYPE_FILTERS.VAMC && sortedServices?.length) {
      return [ALL_SERVICES_VAMC, ...sortedServices];
    }

    return sortedServices;
  };

  /**
   * Entry point for getting services for the autosuggest dropdown
   * after the data fetch
   * @param { string } term
   * @param { string } facilityType
   * @returns {{nameMatch: number, akaMatch: number, commonCondMatch: number, descriptionMatch: number, tricareDescriptionMatch: number, hsdatum:[string, string, string|null, string, string, boolean, boolean, boolean, boolean, number, string, string] }[]}
   */
  const serviceTypeFilter = useCallback(
    (term, facilityType) => {
      // If the typed term is the hardcoded VAMC "All VA health services" option,
      // simply return that option
      if (term === ALL_SERVICES_VAMC[0]) {
        return [ALL_SERVICES_VAMC];
      }

      if (selector.data) {
        const allServicesByFacilityType = filterServicesByFacilityType(
          selector.data,
          facilityType,
        );

        if (!allServicesByFacilityType?.length) {
          return [];
        }

        if (!term?.length) {
          return populateInitialServiceList(
            allServicesByFacilityType,
            facilityType,
          );
        }

        // If the user has typed the minimum number of characters,
        // use the term to filter the matches
        const matches = filterMatches(allServicesByFacilityType, term);

        if (facilityType === FACILITY_TYPE_FILTERS.VAMC && matches?.length) {
          return [ALL_SERVICES_VAMC, ...matches];
        }

        return matches;
      }

      return [];
    },
    // filterServicesByFacilityType, populateInitialServiceList, and filterMatches
    // are plain functions redefined each render; only selector.data drives output
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selector],
  );

  return {
    selector,
    serviceTypeFilter,
  };
}
